import { getAPIURL, handleResponseError } from "./api";

// Константы для статусов согласования
export const TIMESHEET_STATUSES = {
  PENDING: "На рассмотрении",
  APPROVED: "Согласован",
  REJECTED: "Не согласован",
};

/**
 * Создание запроса на согласование табеля
 * @param {Object} timesheetData - Данные для создания согласования
 * @param {number} timesheetData.year - Год
 * @param {number} timesheetData.month - Месяц (1-12)
 * @param {number} timesheetData.group_id - ID отдела/группы
 * @param {number[]} timesheetData.approver_ids - Массив ID согласующих
 * @returns {Promise<Object>} Созданный запрос на согласование
 */
export const createTimesheetApproval = async (timesheetData) => {
  try {
    console.log("Создание запроса на согласование табеля:", timesheetData);

    const response = await fetch(`${getAPIURL()}timesheet/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(timesheetData),
    });

    await handleResponseError(
      response,
      "Ошибка при создании запроса на согласование табеля"
    );

    const data = await response.json();
    console.log("Успешно создан запрос на согласование:", data);
    return data;
  } catch (error) {
    console.error("Ошибка при создании запроса на согласование табеля:", error);
    throw error;
  }
};

/**
 * Получение всех запросов на согласование табелей для конкретного согласующего
 * @param {number} approverId - ID согласующего
 * @returns {Promise<Array>} Массив запросов на согласование
 */
export const getTimesheetApprovalsByApprover = async (approverId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}timesheet/by-approver/${approverId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    await handleResponseError(
      response,
      "Ошибка при получении запросов на согласование"
    );

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении запросов на согласование:", error);
    throw error;
  }
};

/**
 * Обновление статуса конкретного шага согласования
 * @param {number} stepId - ID шага согласования
 * @param {Object} stepData - Данные для обновления шага
 * @param {string} stepData.status - Статус ("approved", "rejected", "pending")
 * @param {string} [stepData.comment] - Комментарий к решению
 * @returns {Promise<Object>} Обновленный шаг согласования
 */
export const updateTimesheetStep = async (stepId, stepData) => {
  try {
    console.log("Обновление шага согласования:", { stepId, stepData });

    const response = await fetch(`${getAPIURL()}timesheet/steps/${stepId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(stepData),
    });

    // Специальная обработка для случая, когда сервер удаляет согласование
    if (!response.ok && response.status === 400) {
      try {
        const errorData = await response.json();
        if (
          errorData.detail &&
          errorData.detail.includes("Согласование удалено")
        ) {
          // Это не ошибка, а успешное удаление согласования
          console.log("Согласование успешно удалено:", errorData.detail);
          return { success: true, deleted: true, message: errorData.detail };
        }
      } catch (jsonError) {
        // Если не удалось распарсить JSON, продолжаем обычную обработку ошибок
      }
    }

    await handleResponseError(
      response,
      "Ошибка при обновлении шага согласования"
    );

    const data = await response.json();
    console.log("Успешно обновлен шаг согласования:", data);
    return data;
  } catch (error) {
    console.error("Ошибка при обновлении шага согласования:", error);
    throw error;
  }
};

/**
 * Получение всех запросов на согласование табелей (для администраторов)
 * @returns {Promise<Array>} Массив всех запросов на согласование
 */
export const getAllTimesheetApprovals = async () => {
  try {
    console.log("Получение всех запросов на согласование табелей");

    const response = await fetch(`${getAPIURL()}timesheet/all`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    await handleResponseError(
      response,
      "Ошибка при получении всех запросов на согласование"
    );

    const data = await response.json();
    console.log("Получены все запросы на согласование:", data);
    return data;
  } catch (error) {
    console.error("Ошибка при получении всех запросов на согласование:", error);
    throw error;
  }
};

/**
 * Получение детальной информации о конкретном запросе на согласование
 * @param {number} timesheetId - ID запроса на согласование
 * @returns {Promise<Object>} Детальная информация о запросе
 */
export const getTimesheetApprovalById = async (timesheetId) => {
  try {
    console.log(
      "Получение детальной информации о запросе на согласование:",
      timesheetId
    );

    const response = await fetch(`${getAPIURL()}timesheet/${timesheetId}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    await handleResponseError(
      response,
      "Ошибка при получении информации о запросе на согласование"
    );

    const data = await response.json();
    console.log("Получена детальная информация о запросе:", data);
    return data;
  } catch (error) {
    console.error(
      "Ошибка при получении информации о запросе на согласование:",
      error
    );
    throw error;
  }
};

/**
 * Утилитарная функция для преобразования статуса на русский язык
 * @param {string} status - Статус (может быть как русский, так и английский)
 * @returns {string} Статус на русском языке
 */
export const getStatusText = (status) => {
  // Если статус уже на русском языке, возвращаем как есть
  if (Object.values(TIMESHEET_STATUSES).includes(status)) {
    return status;
  }

  // Преобразуем английские статусы в русские
  switch (status) {
    case "pending":
      return TIMESHEET_STATUSES.PENDING;
    case "approved":
      return TIMESHEET_STATUSES.APPROVED;
    case "rejected":
      return TIMESHEET_STATUSES.REJECTED;
    default:
      return "Неизвестно";
  }
};

/**
 * Утилитарная функция для получения цвета статуса
 * @param {string} status - Статус (может быть как русский, так и английский)
 * @returns {string} CSS класс для цвета
 */
export const getStatusColor = (status) => {
  // Нормализуем статус к русскому варианту
  const normalizedStatus = getStatusText(status);

  switch (normalizedStatus) {
    case TIMESHEET_STATUSES.PENDING:
      return "status-pending";
    case TIMESHEET_STATUSES.APPROVED:
      return "status-approved";
    case TIMESHEET_STATUSES.REJECTED:
      return "status-rejected";
    default:
      return "status-unknown";
  }
};

/**
 * Утилитарная функция для нормализации статуса к русскому варианту
 * @param {string} status - Статус (может быть как русский, так и английский)
 * @returns {string} Нормализованный статус на русском языке
 */
export const normalizeStatus = (status) => {
  return getStatusText(status);
};

/**
 * Получение списка табелей с фильтрацией
 * @param {Object} params - Параметры фильтрации
 * @param {number} [params.group_id] - ID группы/отдела
 * @param {number} [params.year] - Год
 * @param {number} [params.month] - Месяц (1-12)
 * @param {number} [params.offset] - Смещение для пагинации (по умолчанию 0)
 * @param {number} [params.limit] - Лимит записей (по умолчанию 20)
 * @returns {Promise<Array>} Массив табелей с информацией о согласовании
 */
export const getTimesheetApprovals = async (params = {}) => {
  try {
    console.log("Получение списка табелей с параметрами:", params);

    const queryParams = new URLSearchParams();
    if (params.group_id) queryParams.append("group_id", params.group_id);
    if (params.year) queryParams.append("year", params.year);
    if (params.month) queryParams.append("month", params.month);
    if (params.offset !== undefined)
      queryParams.append("offset", params.offset);
    if (params.limit !== undefined) queryParams.append("limit", params.limit);

    const response = await fetch(
      `${getAPIURL()}timesheet/?${queryParams.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    await handleResponseError(response, "Ошибка при получении списка табелей");

    const data = await response.json();
    console.log("Получен список табелей:", data);
    return data;
  } catch (error) {
    console.error("Ошибка при получении списка табелей:", error);
    throw error;
  }
};

/**
 * Получение списка согласований зарплат (v2) с фильтрацией.
 * @param {Object} params - Параметры фильтрации
 * @param {number} [params.department_id] - ID отдела
 * @param {number} [params.year] - Год
 * @param {number} [params.month] - Месяц (1-12)
 * @param {number} [params.offset] - Смещение для пагинации (по умолчанию 0)
 * @param {number} [params.limit] - Лимит записей (по умолчанию 20, максимум 100)
 * @returns {Promise<Array>} Массив согласований зарплат
 */
export const getSalaryApprovalsV2 = async (params = {}) => {
  try {
    console.log("Получение списка согласований зарплат v2:", params);

    const queryParams = new URLSearchParams();
    if (params.department_id !== undefined && params.department_id !== null) {
      queryParams.append("department_id", params.department_id);
    }
    if (params.year !== undefined && params.year !== null) {
      queryParams.append("year", params.year);
    }
    if (params.month !== undefined && params.month !== null) {
      queryParams.append("month", params.month);
    }
    queryParams.append("offset", params.offset ?? 0);
    queryParams.append("limit", params.limit ?? 20);

    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/?${queryParams.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении списка согласований зарплат",
    );

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Ошибка при получении списка согласований зарплат v2:", error);
    throw error;
  }
};

/**
 * Получение списка согласований зарплат (v2) для конкретного согласующего.
 * @param {number} approverId - ID согласующего
 * @returns {Promise<Array>} Массив согласований зарплат
 */
export const getSalaryApprovalsByApproverV2 = async (approverId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/by-approver/${approverId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении списка согласований зарплат для согласующего",
    );

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(
      "Ошибка при получении согласований зарплат по согласующему:",
      error,
    );
    throw error;
  }
};

/**
 * Итоговые суммы зарплаты по отделениям с созданным согласованием за месяц (v2).
 * @param {number} year - Год
 * @param {number} month - Месяц (1-12)
 * @returns {Promise<Object>} Сводка по отделениям и общий итог
 */
export const getSalaryApprovalMonthTotalsV2 = async (year, month) => {
  try {
    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/totals/${year}/${month}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении итоговых сумм зарплаты за месяц",
    );

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при получении итоговых сумм зарплаты за месяц:",
      error,
    );
    throw error;
  }
};

const appendFilterId = (query, name, value) => {
  if (value != null && value !== "" && value !== "all") {
    query.append(name, String(value));
  }
};

const readFailedResponseMessage = async (response, fallback) => {
  let message = fallback;
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return message;

  const errorData = await response.json().catch(() => null);
  const detail = errorData?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const text = detail.map((item) => item?.msg).filter(Boolean).join("; ");
    if (text) return text;
  }
  if (errorData?.message) return errorData.message;
  return message;
};

/**
 * Сальдо по сотрудникам за период (счёт 3350).
 * date_from / date_to — YYYY-MM-DD.
 * Один user_id: выплаты подтягиваются из Finansist по контрагенту.
 * Отделение или группа без сотрудника: выплаты из кэша.
 * @param {{ dateFrom: string, dateTo: string, departmentId?: number|string, groupId?: number|string, userId?: number|string }} params
 */
export const getSalaryPortalSaldoV2 = async ({
  dateFrom,
  dateTo,
  departmentId,
  groupId,
  userId,
} = {}) => {
  const query = new URLSearchParams();
  query.append("date_from", dateFrom);
  query.append("date_to", dateTo);
  appendFilterId(query, "department_id", departmentId);
  appendFilterId(query, "group_id", groupId);
  appendFilterId(query, "user_id", userId);

  try {
    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/saldo?${query.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const message = await readFailedResponseMessage(
        response,
        "Ошибка при получении сальдо по сотрудникам",
      );
      throw new Error(`${message} (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении сальдо по сотрудникам:", error);
    throw error;
  }
};

/**
 * Синхронизация зарплатных выплат Finansist в кэш.
 * Без user_id — полный обход за период. С user_id — один контрагент.
 * @param {{ dateFrom?: string, dateTo?: string, userId?: number|string }} [params]
 */
export const syncFinansistPayrollPayments = async ({
  dateFrom,
  dateTo,
  userId,
} = {}) => {
  const query = new URLSearchParams();
  if (dateFrom) query.append("date_from", dateFrom);
  if (dateTo) query.append("date_to", dateTo);
  appendFilterId(query, "user_id", userId);

  try {
    const response = await fetch(
      `${getAPIURL()}finansist/payroll-payments/sync?${query.toString()}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const message = await readFailedResponseMessage(
        response,
        "Ошибка при обновлении выплат",
      );
      throw new Error(`${message} (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при синхронизации выплат Finansist:", error);
    throw error;
  }
};

/**
 * Получение детальной информации по согласованию зарплаты (v2).
 * @param {number} approvalId - ID согласования
 * @returns {Promise<Object>} Детали согласования и табличные данные
 */
export const getSalaryApprovalDetailV2 = async (approvalId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/approvals/${approvalId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении деталей согласования зарплаты",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении деталей salary approval v2:", error);
    throw error;
  }
};

/**
 * Обновление шага согласования зарплаты (v2).
 * @param {number} stepId - ID шага
 * @param {Object} body
 * @param {string} body.status - Статус (как в согласовании табелей, см. TIMESHEET_STATUSES)
 * @param {string} [body.comment] - Комментарий
 * @returns {Promise<Object>} Обновлённое согласование
 */
export const patchSalaryApprovalStepV2 = async (stepId, body) => {
  try {
    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/steps/${stepId}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
    );

    await handleResponseError(
      response,
      "Ошибка при обновлении шага согласования зарплаты",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при PATCH salary-approval-v2/steps:", error);
    throw error;
  }
};

/**
 * Список согласований зарплаты компании (v2), только для директора.
 * @param {Object} params
 * @param {number} [params.year]
 * @param {number} [params.month]
 * @returns {Promise<Array>}
 */
export const getCompanySalaryApprovalsV2 = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.year !== undefined && params.year !== null) {
      queryParams.append("year", params.year);
    }
    if (params.month !== undefined && params.month !== null) {
      queryParams.append("month", params.month);
    }
    const query = queryParams.toString();
    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/company-approvals${query ? `?${query}` : ""}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении списка согласований компании",
    );
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Ошибка при получении company approvals:", error);
    throw error;
  }
};

/**
 * Детали согласования зарплаты компании (v2).
 * @param {number} approvalId
 * @returns {Promise<Object>}
 */
export const getCompanySalaryApprovalDetailV2 = async (approvalId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/company-approvals/${approvalId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    await handleResponseError(
      response,
      "Ошибка при получении деталей согласования компании",
    );
    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении company approval detail:", error);
    throw error;
  }
};

/**
 * Согласование/отклонение компании директором (v2).
 * @param {number} approvalId
 * @param {Object} body
 * @param {string} body.status
 * @param {string} [body.comment]
 * @returns {Promise<Object>}
 */
export const patchCompanySalaryApprovalV2 = async (approvalId, body) => {
  try {
    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/company-approvals/${approvalId}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
    );
    await handleResponseError(
      response,
      "Ошибка при обновлении согласования компании",
    );
    return await response.json();
  } catch (error) {
    console.error("Ошибка при PATCH company approval:", error);
    throw error;
  }
};

/**
 * Пересчёт кэша зарплаты за месяц (v2) с учетом периода выплат.
 * @param {Object} params
 * @param {number} params.year - Год
 * @param {number} params.month - Месяц (1–12)
 * @param {number[]} [params.userIds] - Опционально: пересчитать только выбранных сотрудников
 * @param {string} [params.paymentsDateFrom] - Дата начала периода выплат (YYYY-MM-DD)
 * @param {string} [params.paymentsDateTo] - Дата конца периода выплат (YYYY-MM-DD)
 * @param {number} [params.paymentsTailDays] - Опционально: хвост дней
 * @param {number} [params.paymentsHeadDays] - Опционально: head дней
 * @returns {Promise<string|unknown>} Ответ API (часто строка-сообщение в JSON)
 */
export const rebuildSalaryApprovalPortalCache = async ({
  year,
  month,
  userIds,
  paymentsDateFrom,
  paymentsDateTo,
  paymentsTailDays,
  paymentsHeadDays,
}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("year", year);
    queryParams.append("month", month);
    if (Array.isArray(userIds) && userIds.length > 0) {
      userIds.forEach((userId) => {
        queryParams.append("user_ids", String(userId));
      });
    }
    if (paymentsDateFrom) {
      queryParams.append("payments_date_from", paymentsDateFrom);
    }
    if (paymentsDateTo) {
      queryParams.append("payments_date_to", paymentsDateTo);
    }
    if (paymentsTailDays !== undefined && paymentsTailDays !== null) {
      queryParams.append("payments_tail_days", String(paymentsTailDays));
    }
    if (paymentsHeadDays !== undefined && paymentsHeadDays !== null) {
      queryParams.append("payments_head_days", String(paymentsHeadDays));
    }

    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/recalculate-month?${queryParams.toString()}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при подготовке таблицы расчёта зарплаты за месяц",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при POST salary-approval-v2/recalculate-month:", error);
    throw error;
  }
};

/**
 * Создание запроса на согласование зарплаты (v2).
 * @param {Object} payload
 * @param {number} payload.year - Год
 * @param {number} payload.month - Месяц (1-12)
 * @param {number} payload.department_id - ID отдела
 * @param {number[]} payload.approver_ids - ID согласующих
 * @param {number} payload.company_approver_id - ID согласующего по компании
 * @returns {Promise<Object>}
 */
export const createSalaryApprovalV2 = async (payload) => {
  try {
    const response = await fetch(`${getAPIURL()}salary-approval-v2/`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    await handleResponseError(
      response,
      "Ошибка при создании согласования зарплаты",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при создании salary approval v2:", error);
    throw error;
  }
};

/**
 * Удаление согласования зарплаты (v2).
 * @param {number} approvalId - ID согласования
 * @returns {Promise<Object>}
 */
export const deleteSalaryApprovalV2 = async (approvalId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/${approvalId}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при удалении согласования зарплаты",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при DELETE salary-approval-v2:", error);
    throw error;
  }
};

/**
 * Получение превью зарплаты по отделению (v2).
 * @param {number} departmentId - ID отделения
 * @param {Object} params - Параметры запроса
 * @param {number} params.year - Год
 * @param {number} params.month - Месяц (1-12)
 * @returns {Promise<Object>} Превью данных зарплаты по отделению
 */
export const getSalaryDepartmentPreviewV2 = async (departmentId, params) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("year", params.year);
    queryParams.append("month", params.month);

    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/department/${departmentId}/preview?${queryParams.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении превью согласования зарплаты",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении превью зарплаты по отделению:", error);
    throw error;
  }
};

/**
 * Сохранение ручных правок портального кэша зарплаты (v2).
 * @param {Object} payload
 * @param {number} payload.user_id - ID сотрудника
 * @param {number} payload.department_id - ID отдела
 * @param {number} payload.year - Год
 * @param {number} payload.month - Месяц (1-12)
 * @param {Object} payload.changes - Измененные поля
 * @param {string} payload.reason - Причина изменения
 * @returns {Promise<Object>}
 */
export const patchSalaryApprovalPortalCache = async (payload) => {
  try {
    const response = await fetch(`${getAPIURL()}salary-approval-v2/portal-cache`, {
      method: "PATCH",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    await handleResponseError(
      response,
      "Ошибка при сохранении изменений портального кэша зарплаты",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при PATCH salary-approval-v2/portal-cache:", error);
    throw error;
  }
};

/**
 * Удаление одной строки портального кэша зарплаты (v2).
 * @param {Object} params
 * @param {number} params.user_id - ID сотрудника
 * @param {number} params.calculation_year - Год расчёта
 * @param {number} params.calculation_month - Месяц расчёта (1-12)
 * @param {number} params.department_id - ID отделения
 * @returns {Promise<Object>}
 */
export const deleteSalaryApprovalPortalCache = async ({
  user_id,
  calculation_year,
  calculation_month,
  department_id,
}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("user_id", String(user_id));
    queryParams.append("calculation_year", String(calculation_year));
    queryParams.append("calculation_month", String(calculation_month));
    queryParams.append("department_id", String(department_id));

    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/portal-cache?${queryParams.toString()}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при удалении строки кэша зарплаты",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при DELETE salary-approval-v2/portal-cache:", error);
    throw error;
  }
};

/**
 * Обновить процент доплаты за замещение (v2).
 * @param {number} paymentId - ID substitution_payment
 * @param {Object} payload
 * @param {number} payload.payment_percent - Процент доплаты
 * @param {string} payload.reason - Причина изменения
 * @returns {Promise<Object>}
 */
export const patchSubstitutionPaymentPercentV2 = async (paymentId, payload) => {
  try {
    const response = await fetch(
      `${getAPIURL()}salary-approval-v2/substitution-payment/${paymentId}/percent`,
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    await handleResponseError(
      response,
      "Ошибка при обновлении процента доплаты за замещение",
    );

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при PATCH salary-approval-v2/substitution-payment/{id}/percent:",
      error,
    );
    throw error;
  }
};

/**
 * Список ID согласований зарплаты, уже отправленных в Finansist за период.
 * @param {Object} params
 * @param {number} params.calculation_year
 * @param {number} params.month
 * @returns {Promise<{ calculation_year: number, month: number, approval_ids: number[] }>}
 */
export const getPayrollDocumentExternalApprovalIds = async ({
  calculation_year,
  month,
}) => {
  try {
    const query = new URLSearchParams({
      calculation_year: String(calculation_year),
      month: String(month),
    });

    const response = await fetch(
      `${getAPIURL()}finansist/mapping/payroll-document-external-mappings/approval-ids?${query.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении списка отправленных в Finansist согласований",
    );

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при GET finansist/mapping/payroll-document-external-mappings/approval-ids:",
      error,
    );
    throw error;
  }
};

/**
 * Удаление маппингов payroll-document-external по approval_id (+ опционально user_id).
 * @param {Object} params
 * @param {number} params.approval_id
 * @param {number} [params.user_id]
 * @param {number} [params.calculation_year]
 * @param {number} [params.calculation_month]
 * @returns {Promise<{ deleted_count: number, approval_id: number, user_id: number, calculation_year: number, calculation_month: number }>}
 */
export const deletePayrollDocumentExternalMappings = async ({
  approval_id,
  user_id,
  calculation_year,
  calculation_month,
}) => {
  try {
    const query = new URLSearchParams();
    if (user_id != null) query.set("user_id", String(user_id));
    if (calculation_year != null)
      query.set("calculation_year", String(calculation_year));
    if (calculation_month != null)
      query.set("calculation_month", String(calculation_month));
    const qs = query.toString();
    const url = `${getAPIURL()}finansist/mapping/payroll-document-external-mappings/by-approval/${approval_id}${qs ? `?${qs}` : ""}`;

    const response = await fetch(url, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    await handleResponseError(
      response,
      "Ошибка при удалении маппингов payroll-document-external",
    );

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при DELETE payroll-document-external-mappings:",
      error,
    );
    throw error;
  }
};

/**
 * Пуш согласованной зарплаты в Finansist.
 * Источник данных: salary_approval_portal_month_cache (approved snapshot).
 * @param {Object} payload
 * @param {number} payload.approval_id - ID согласования зарплаты
 * @param {number[]} [payload.user_ids] - Опционально: отправить только указанных сотрудников
 * @param {boolean} [payload.dry_run] - Тестовый прогон без создания документов в Finansist
 * @param {"skip"|"error"} [payload.on_missing_contragent] - Поведение при отсутствии контрагента
 * @param {boolean} [payload.include_documents_in_response] - Возвращать созданные документы в ответе
 * @returns {Promise<Object>}
 */
export const pushPayrollApprovalToFinansist = async (payload) => {
  try {
    const requestPayload = {
      ...payload,
      on_missing_contragent: payload?.on_missing_contragent ?? "skip",
      include_documents_in_response:
        payload?.include_documents_in_response ?? true,
    };

    const response = await fetch(`${getAPIURL()}finansist/push-payroll/approval/new`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestPayload),
    });

    await handleResponseError(response, "Ошибка при отправке зарплаты в Finansist");

    return await response.json();
  } catch (error) {
    console.error("Ошибка при POST finansist/push-payroll/approval/new:", error);
    throw error;
  }
};

/**
 * Каталог проектов Finansist (раздел / проект в аналитике).
 * @param {Object} [params]
 * @param {number} [params.offset=0]
 * @param {boolean} [params.allPages=true]
 * @param {number} [params.maxPages=30]
 * @returns {Promise<unknown>}
 */
export const getFinansistCatalogProjects = async ({
  offset = 0,
  allPages = true,
  maxPages = 30,
} = {}) => {
  try {
    const query = new URLSearchParams({
      offset: String(offset),
      all_pages: String(allPages),
      max_pages: String(maxPages),
    });

    const response = await fetch(
      `${getAPIURL()}finansist/catalog/projects?${query.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении проектов Finansist",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при GET finansist/catalog/projects:", error);
    throw error;
  }
};

/**
 * Каталог контрагентов Finansist.
 * @param {Object} [params]
 * @param {number} [params.offset=0]
 * @param {boolean} [params.allPages=true]
 * @param {number} [params.maxPages=40]
 * @returns {Promise<unknown>}
 */
export const getFinansistCatalogContragents = async ({
  offset = 0,
  allPages = true,
  maxPages = 40,
} = {}) => {
  try {
    const query = new URLSearchParams({
      offset: String(offset),
      all_pages: String(allPages),
      max_pages: String(maxPages),
    });

    const response = await fetch(
      `${getAPIURL()}finansist/catalog/contragents?${query.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении контрагентов Finansist",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при GET finansist/catalog/contragents:", error);
    throw error;
  }
};

/**
 * Синхронизация привязок пользователей портала к контрагентам Finansist.
 * @param {Object} [params]
 * @param {number[]} [params.userIds] — если не передан или пустой, синхронизируются все подходящие пользователи
 * @param {boolean} [params.allPages=true]
 * @param {number} [params.maxPages=100]
 * @returns {Promise<{
 *   total_users_without_mapping: number,
 *   created_mappings: number,
 *   items: Array,
 *   duplicates: Array,
 *   unresolved: Array
 * }>}
 */
export const syncUserContragentMappingsFromFinansist = async ({
  userIds,
  allPages = true,
  maxPages = 100,
} = {}) => {
  try {
    const body = {
      all_pages: allPages,
      max_pages: maxPages,
    };

    if (Array.isArray(userIds) && userIds.length > 0) {
      body.user_ids = userIds.map((id) => Number(id));
    }

    const response = await fetch(
      `${getAPIURL()}finansist/mapping/user-contragents/sync-from-finansist`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
    );

    await handleResponseError(
      response,
      "Ошибка при синхронизации контрагентов Finansist",
    );

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при POST finansist/mapping/user-contragents/sync-from-finansist:",
      error,
    );
    throw error;
  }
};

/**
 * Получение каталога статей (fact streams) из Finansist.
 * @returns {Promise<Array>} Массив статей
 */
export const getFinansistFactStreams = async () => {
  try {
    const response = await fetch(`${getAPIURL()}finansist/catalog/fact-streams`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    await handleResponseError(response, "Ошибка при получении статей Finansist");

    const data = await response.json();
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.listFactStream)) return data.listFactStream;
    return [];
  } catch (error) {
    console.error("Ошибка при GET finansist/catalog/fact-streams:", error);
    throw error;
  }
};

/**
 * Детализация зарплаты сотрудника за месяц (v2).
 * @param {number} userId - ID пользователя
 * @param {Object} params
 * @param {number} params.year - Год
 * @param {number} params.month - Месяц (1–12)
 * @returns {Promise<Object>}
 */
export const getSalaryEmployeeDetailV2 = async (userId, params) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("year", params.year);
    queryParams.append("month", params.month);

    const response = await fetch(
      `${getAPIURL()}salary-v2/employee/${userId}?${queryParams.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении детализации зарплаты сотрудника",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении детализации зарплаты сотрудника v2:", error);
    throw error;
  }
};

/**
 * История начислений сотрудника за 12 месяцев (v2).
 * @param {number} userId - ID пользователя
 * @param {Object} [params]
 * @param {number} [params.reference_year] - Базовый год для отсчета 12 месяцев
 * @param {number} [params.reference_month] - Базовый месяц (1-12) для отсчета 12 месяцев
 * @returns {Promise<Object>}
 */
export const getSalaryEmployeeHistoryV2 = async (userId, params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (Number.isInteger(Number(params.reference_year))) {
      queryParams.append("reference_year", String(params.reference_year));
    }
    if (Number.isInteger(Number(params.reference_month))) {
      queryParams.append("reference_month", String(params.reference_month));
    }

    const queryString = queryParams.toString();
    const response = await fetch(
      `${getAPIURL()}salary-v2/employee/${userId}/history${queryString ? `?${queryString}` : ""}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await handleResponseError(
      response,
      "Ошибка при получении истории зарплаты сотрудника",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении истории зарплаты сотрудника v2:", error);
    throw error;
  }
};

/**
 * Обновить запись истории зарплаты сотрудника (v2).
 * @param {number} userId - ID пользователя
 * @param {Object} payload
 * @param {number} payload.year - Год записи
 * @param {number} payload.month - Месяц записи (1-12)
 * @param {number} payload.amount - Новая сумма
 * @param {string} payload.reason - Причина изменения
 * @returns {Promise<Object>}
 */
export const patchSalaryEmployeeHistoryV2 = async (userId, payload) => {
  try {
    const response = await fetch(
      `${getAPIURL()}salary-v2/employee/${userId}/history`,
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    await handleResponseError(
      response,
      "Ошибка при обновлении истории зарплаты сотрудника",
    );

    return await response.json();
  } catch (error) {
    console.error("Ошибка при PATCH salary-v2/employee/{user_id}/history:", error);
    throw error;
  }
};

/**
 * Расчёт заработной платы РП без отчетов о рассчетах (переработки не входят в расчет)
 * Оптимизированная версия, использует salary_history, прикрепленный к каждому отчету
 * @param {Object} params - Параметры запроса
 * @param {number} [params.group_id] - ID группы/отдела
 * @param {number} params.year - Год
 * @param {number} params.month - Месяц (1-12)
 * @returns {Promise<Object>} Данные о расчете зарплаты
 */
export const calculateWorkingSalary = async (params) => {
  try {
    console.log("Расчет заработной платы с параметрами:", params);

    const queryParams = new URLSearchParams();
    if (params.group_id) queryParams.append("group_id", params.group_id);
    queryParams.append("year", params.year);
    queryParams.append("month", params.month);

    const response = await fetch(
      `${getAPIURL()}timesheet/working/salary/calculate/optimized?${queryParams.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    await handleResponseError(response, "Ошибка при расчете заработной платы");

    const data = await response.json();
    console.log("Получены данные расчета зарплаты:", data);
    return data;
  } catch (error) {
    console.error("Ошибка при расчете заработной платы:", error);
    throw error;
  }
};

/**
 * Расчёт заработной платы РП с полным отчетом (переработки не входят в расчет)
 * @param {Object} params - Параметры запроса
 * @param {number} [params.group_id] - ID группы/отдела
 * @param {number} params.year - Год
 * @param {number} params.month - Месяц (1-12)
 * @returns {Promise<string>} Полный отчет в виде строки (JSON или другой формат)
 */
export const calculateWorkingSalaryWithFullReport = async (params) => {
  try {
    console.log("Расчет заработной платы с полным отчетом, параметры:", params);

    const queryParams = new URLSearchParams();
    if (params.group_id) queryParams.append("group_id", params.group_id);
    queryParams.append("year", params.year);
    queryParams.append("month", params.month);

    const response = await fetch(
      `${getAPIURL()}timesheet/working/salary/calculate-with-full-report?${queryParams.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    await handleResponseError(
      response,
      "Ошибка при получении полного отчета о расчете зарплаты"
    );

    // Проверяем Content-Type заголовка
    const contentType = response.headers.get("content-type");
    console.log("Content-Type ответа:", contentType);

    // Эндпоинт возвращает строку, возможно JSON-строку
    const text = await response.text();
    console.log(
      "Получен полный отчет (первые 500 символов):",
      text.substring(0, 500)
    );
    console.log("Длина ответа:", text.length);
    console.log("Тип данных в ответе:", typeof text);

    // Если Content-Type указывает на JSON, пытаемся распарсить
    if (contentType && contentType.includes("application/json")) {
      try {
        const parsed = JSON.parse(text);
        console.log("Успешно распарсен JSON:", parsed);
        return parsed;
      } catch (e) {
        console.error("Ошибка парсинга JSON:", e);
        console.error("Текст ответа:", text);
        throw new Error("Не удалось распарсить JSON ответ от сервера");
      }
    }

    // Пытаемся распарсить как JSON в любом случае (на случай, если заголовок не установлен)
    try {
      const parsed = JSON.parse(text);
      console.log(
        "Успешно распарсен как JSON (без проверки Content-Type):",
        parsed
      );
      return parsed;
    } catch (e) {
      console.warn("Ответ не является JSON, возвращаем как строку");
      console.warn("Текст ответа:", text);
      return text;
    }
  } catch (error) {
    console.error(
      "Ошибка при получении полного отчета о расчете зарплаты:",
      error
    );
    throw error;
  }
};
